// Consolidates id + answer fetch configuration used by ActivityGroupProvider
/**
 * Derives a normalized configuration object for fetching answers related to an activity group.
 * It resolves the effective activity group id, answer id and CIS user to query based on
 * transient (requested) state, route / caller provided props and currently active identifiers.
 *
 * Preference rules:
 * 1. For activity group id prefer `state.requestedActivityGroupId` (freshly requested while opening a feed)
 *    over the possibly stale `relevantActivityGroupId` supplied by the route.
 * 2. For activity group answer id prefer a valid numeric `parsedAnswerId`; otherwise fall back to
 *    `state.activityGroupAnswerId` (previously stored / selected answer).
 * 3. For CIS user id prefer explicitly supplied `cisId`, then any `state.requestedAnswerOwnerCisId`,
 *    finally the `activeCisId` (current signed-in user context).
 *
 * @param {Object} params
 * @param {Object} params.state - Provider / reducer state containing transient request fields.
 * @param {number|undefined} params.parsedAnswerId - Parsed numeric answer id from route / URL (may be NaN / undefined).
 * @param {string|number|undefined} params.cisId - Explicit CIS id to scope the answer query (highest priority if provided).
 * @param {string|number|undefined} params.activeCisId - Currently active / signed-in CIS id (lowest priority fallback).
 * @param {string|number|undefined} params.relevantActivityGroupId - Activity group id derived from routing context.
 * @returns {Object} config
 * @returns {boolean} config.isValidParsedAnswerId - Whether the provided parsedAnswerId is a valid number.
 * @returns {string|number|undefined} config.effectiveActivityGroupId - Resolved activity group id to use for queries.
 * @returns {number|undefined} config.effectiveActivityGroupAnswerId - Resolved answer id to use for queries.
 * @returns {string|number|undefined} config.cisUserForQuery - Resolved CIS user id to scope the answer fetch.
 */
export default function useAnswerFetchConfig({ state, parsedAnswerId, cisId, activeCisId, relevantActivityGroupId }) {
  const isValidParsedAnswerId = typeof parsedAnswerId === 'number' && !Number.isNaN(parsedAnswerId)
  // Precedence handled centrally (see getEffectiveActivityGroupId.js): requested overrides route.
  const effectiveActivityGroupId = state?.requestedActivityGroupId || relevantActivityGroupId
  const effectiveActivityGroupAnswerId = isValidParsedAnswerId ? parsedAnswerId : state?.activityGroupAnswerId
  const cisUserForQuery = cisId ?? state?.requestedAnswerOwnerCisId ?? activeCisId
  return { isValidParsedAnswerId, effectiveActivityGroupId, effectiveActivityGroupAnswerId, cisUserForQuery }
}
